"use client";

import { ArrowRight, TrendingUp, DollarSign, Clock, ShieldCheck, Building2, CalendarDays } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RevenueGrowthEngine() {
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const metrics = [{
    value: "700+",
    label: "Hotels Managed",
    description: "Across India and international leisure markets",
    icon: Building2
  }, {
    value: "15–25%",
    label: "Net Revenue Improvement",
    description: "Verified across client engagements",
    icon: TrendingUp
  }, {
    value: "15+",
    label: "Years in Hotel Revenue",
    description: "Hands-on commercial strategy",
    icon: CalendarDays
  }, {
    value: "14-Day",
    label: "Audit Turnaround",
    description: "OTA and distribution gap review",
    icon: Clock
  }];
  return <section data-section-id="486" id="revenue-growth-engine" className="relative w-full overflow-hidden bg-background text-foreground py-20 sm:py-24 lg:py-32">
      {/* Background Ambience & Grid Pattern */}
      <div aria-hidden="true" className="hidden sm:block pointer-events-none absolute inset-0 opacity-[0.07]" style={{
      backgroundImage: `radial-gradient(circle at 2px 2px, hsl(43 46% 55%) 1px, transparent 0)`,
      backgroundSize: "40px 40px"
    }} />
      <div aria-hidden="true" className="hidden sm:block pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[650px] h-[400px] bg-secondary/15 blur-[120px] rounded-full" />

      <div className="relative mx-auto max-w-[1280px] px-6 sm:px-10 lg:px-12">
        <div ref={heroRef} className={`flex flex-col items-center text-center transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[30px]"}`}>
          {/* Gold-Bordered Eyebrow Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-secondary/40 bg-secondary/10 text-secondary mb-6 shadow-sm">
            <TrendingUp className="size-3.5 text-secondary" />
            <span className="font-mono text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em]">
              Independent Hotel Revenue Management &amp; OTA Distribution
            </span>
          </div>

          {/* Centered H1 */}
          <h1 className="font-serif text-[clamp(2.15rem,4.5vw,3.75rem)] font-extrabold leading-[1.18] tracking-tight max-w-4xl text-foreground mb-6">
            Stop Losing Hotel Revenue to Hidden OTA Discounts and Static Rates
          </h1>

          {/* Supporting copy (verbatim two paragraphs) */}
          <div className="max-w-3xl space-y-4 mb-8 sm:mb-10 text-muted-foreground font-sans text-base sm:text-lg md:text-xl leading-relaxed">
            <p>
              Most independent hotels leak 12% to 20% of their top line through unauthorized channel discounting and poor pricing timing. I help boutique hotels and resorts take back control of their inventory, protect rate parity, and maximize RevPAR.
            </p>
          </div>

          {/* Dual CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto mb-4">
            <Button asChild size="lg" className="w-full sm:w-auto h-13 px-8 bg-secondary text-secondary-foreground hover:bg-secondary/90 font-bold rounded-full shadow-md transition-all duration-300 group">
              <Link to="/ContactPage#start-your-revenue-journey">
                Request a Free Revenue Audit
                <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-13 px-8 border-border/80 bg-background/50 hover:bg-secondary/10 hover:border-secondary text-foreground font-bold rounded-full transition-all duration-300">
              <Link to="#expertise-in-action">View Hotel Case Studies</Link>
            </Button>
          </div>

          {/* Trust proof strip directly beneath the buttons */}
          <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-muted-foreground mb-14 sm:mb-16 text-center max-w-2xl px-4">
            <ShieldCheck className="size-4 text-secondary shrink-0" />
            <span>
              Trusted by 700+ hotels across India and international leisure markets · Verified 15–25% net revenue improvement.
            </span>
          </div>

          {/* 4-Column Glassmorphism Metrics Bar (2x2 on Mobile, 4-col on Desktop) */}
          <div className="w-full max-w-5xl grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {metrics.map((metric, i) => <div key={metric.label} data-index={i} className="group relative flex flex-col items-center sm:items-start text-center sm:text-left p-5 sm:p-6 rounded-xl bg-card/40 backdrop-blur-md border border-border/40 hover:border-secondary/60 hover:bg-card/70 transition-all duration-300 shadow-md hover:shadow-lg">
                <div className="flex items-center justify-between w-full mb-3">
                  <div className="flex items-center justify-center size-9 rounded-lg bg-secondary/15 border border-secondary/30 text-secondary transition-transform duration-300 group-hover:scale-110">
                    <metric.icon className="size-4.5" />
                  </div>
                </div>
                <div className="font-serif text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground tracking-tight mb-1 group-hover:text-secondary transition-colors duration-300">
                  {metric.value}
                </div>
                <div className="font-mono text-xs font-bold text-secondary uppercase tracking-wider mb-1">
                  {metric.label}
                </div>
                <div className="font-sans text-[11px] sm:text-xs text-muted-foreground leading-snug">
                  {metric.description}
                </div>

                {/* Subtle gold glow line on top border on hover */}
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-secondary/0 to-transparent group-hover:via-secondary transition-all duration-500 rounded-t-xl" />
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}